import {Avatar} from "@mui/material";
import {COLORS} from "~/style/colors-ts";
import {useSupplierLogoQuery} from "../hooks/useSupplierLogoQuery";

/** "Citrus Atlantic SA" → "CA"; a single-word name falls back to its first two letters. */
function getSupplierInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    const initials = words.length > 1
        ? words[0][0] + words[1][0]
        : words[0].slice(0, 1);

    return initials.toUpperCase();
}

type SupplierNameAvatarProps = {
    name: string;
    /** The entity to load the logo of. Omit for an initials-only tile. */
    entityId?: number;
    /**
     * `SupplierEntity.logo_url` (§2.1). Only its emptiness is read: `''` means there is no logo on
     * file, so no request is made for this row. The image itself is fetched by entity id, since the
     * endpoint needs the app's auth header and so cannot be hit from the `src` attribute.
     */
    logoUrl?: string;
    /** Tile edge in px — 24 fits a small (36 px) grid row. */
    size?: number;
};

/** Rounded-square tile shown ahead of a supplier's name: its company logo, or initials without one. */
export function SupplierNameAvatar({name, entityId, logoUrl, size = 30}: SupplierNameAvatarProps) {
    const {data: logoSrc} = useSupplierLogoQuery(entityId, Boolean(logoUrl));
    // An empty `logo_url` means initials. A disabled query still returns its last data URL.
    const src = logoUrl ? logoSrc : undefined;

    return (
        <Avatar
            variant="rounded"
            aria-hidden
            // A logo that 404s or decodes badly falls through to the initials below, same as no logo.
            src={src}
            slotProps={{
                img: {
                    loading: 'lazy',
                    // Logos are rarely square — fit the whole mark inside the tile instead of cropping it.
                    style: {objectFit: 'contain', borderRadius: `8px`},
                },
            }}
            sx = {{
                flexShrink: 0,
                width: size,
                height: size,
                borderRadius: `${Math.round(size / 4)}px`,
                background:  `linear-gradient(180deg, ${COLORS.white[900]}, ${COLORS.grey[50]})`,
                border: !src ? `1px solid ${COLORS.grey[100]}` : 'none',
                color: COLORS.grey[900],
                fontSize: 11.5,
                fontWeight: 500,
                letterSpacing: '0.02em',
                overflow: 'hidden',
            }}
        >
            {getSupplierInitials(name)}
        </Avatar>
    );
}
